// hooks/useCart.ts

import { useState, useEffect, useCallback } from 'react';
import { Cart } from '@/types/shopify-cart';
import { toast } from 'react-hot-toast';
import { 
  clearAuthCookiesClient, 
  handleTokenExpiry, 
  saveWorkState,
  proactiveTokenRefresh,
  getTokenStatus 
} from '@/lib/auth/token-validation';

// Cart Attributes type definition
export interface CartAttributes {
  [key: string]: string;
}

// Transform Cart to legacy CartItem format with handle
function transformCartToLegacyFormat(cart: Cart) {
  return cart.lines.edges.map(({ node }) => ({
    id: node.id, // This is the lineId for cart operations
    productId: node.merchandise.product.id,
    variantId: node.merchandise.id,
    title: node.merchandise.product.title,
    variantTitle: node.merchandise.title,
    price: parseFloat(node.merchandise.price.amount),
    quantity: node.quantity,
    image: node.merchandise.product.featuredImage?.url || '',
    handle: node.merchandise.product.handle || '', // Added handle field
  }));
}

// Cart attributes array to object conversion
function transformAttributesToObject(attributes?: Array<{ key: string; value: string }>): CartAttributes {
  if (!attributes) return {};
  return attributes.reduce((acc, attr) => {
    acc[attr.key] = attr.value;
    return acc;
  }, {} as CartAttributes);
}

export function useCart() {
  const [cart, setCart] = useState<Cart | null>(null);
  const [cartAttributes, setCartAttributes] = useState<CartAttributes>({});
  const [cartNote, setCartNote] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [customerAccessToken, setCustomerAccessToken] = useState<string | null>(null);

  // cartAttributes 업데이트 추적
  const setCartAttributesWithLog = (attributes: CartAttributes) => {
    console.log('setCartAttributes called with:', JSON.stringify(attributes, null, 2));
    setCartAttributes(attributes);
  };

  // Define all functions before using them
  const fetchCart = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/cart');
      const data = await response.json();
      
      if (data.cart) {
        setCart(data.cart);
        
        // 초기화가 진행 중인지 확인
        const hasInitialized = sessionStorage.getItem('cartInitialized');
        const initializationComplete = sessionStorage.getItem('cartInitializationComplete');
        
        console.log('fetchCart: Checking initialization flags', {
          hasInitialized,
          initializationComplete,
          cartAttributesFromServer: data.cart.attributes ? data.cart.attributes.length : 0,
          cartNoteFromServer: data.cart.note || 'none'
        });
        
        // 초기화가 완료된 경우에만 cartAttributes와 cartNote 업데이트
        if (hasInitialized && initializationComplete) {
          console.log('fetchCart: Initialization complete, updating attributes and note');
          // Cart attributes conversion
          const attrs = transformAttributesToObject(data.cart.attributes);
          setCartAttributesWithLog(attrs);
          // Set cart note
          setCartNote(data.cart.note || '');
        } else {
          console.log('Cart data loaded but initialization not complete, skipping attributes/note update');
        }
      }
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const createNewCart = async (items?: Array<{ variantId: string; quantity: number }>) => {
    try {
      const response = await fetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      
      const data = await response.json();
      if (data.cart) {
        setCart(data.cart);
        
        // 초기화가 진행 중인지 확인
        const hasInitialized = sessionStorage.getItem('cartInitialized');
        const initializationComplete = sessionStorage.getItem('cartInitializationComplete');
        
        // 초기화가 완료된 경우에만 cartAttributes 업데이트
        if (hasInitialized && initializationComplete) {
          const attrs = transformAttributesToObject(data.cart.attributes);
          setCartAttributesWithLog(attrs);
        }
        
        return data.cart;
      }
    } catch (error) {
      console.error('Failed to create cart:', error);
      throw error;
    }
  };

  const updateBuyerIdentity = async () => {
    if (!cart) return;
    
    try {
      const response = await fetch('/api/cart', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}), // Customer access token is now sent via cookie
      });
      
      const data = await response.json();
      if (data.cart) {
        setCart(data.cart);
        
        // 초기화가 진행 중인지 확인
        const hasInitialized = sessionStorage.getItem('cartInitialized');
        const initializationComplete = sessionStorage.getItem('cartInitializationComplete');
        
        // 초기화가 완료된 경우에만 cartAttributes 업데이트
        if (hasInitialized && initializationComplete) {
          const attrs = transformAttributesToObject(data.cart.attributes);
          setCartAttributesWithLog(attrs);
        }
      }
    } catch (error) {
      console.error('Failed to update buyer identity:', error);
    }
  };

  // Update cart attributes - PATCH method
  const updateCartAttributes = async (attributes: CartAttributes) => {
    if (!cart) {
      console.warn('No cart available to update attributes');
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch('/api/cart', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update attributes');
      }

      const data = await response.json();
      
      if (data.cart) {
        setCart(data.cart);
        const attrs = transformAttributesToObject(data.cart.attributes);
        setCartAttributesWithLog(attrs);
        
        // 초기화 플래그 설정 (카트 페이지에서 호출된 경우)
        const hasInitialized = sessionStorage.getItem('cartInitialized');
        if (hasInitialized) {
          sessionStorage.setItem('cartInitializationComplete', 'true');
        }
      }
      
      return cartAttributes;
    } catch (error) {
      console.error('Failed to update cart attributes:', error);
      toast.error('Failed to update delivery details');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Update cart note
  const updateCartNote = async (note: string) => {
    if (!cart) {
      console.warn('No cart available to update note');
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch('/api/cart/note', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update note');
      }

      const data = await response.json();
      
      if (data.cart) {
        setCart(data.cart);
        setCartNote(data.cart.note || '');
        const attrs = transformAttributesToObject(data.cart.attributes);
        setCartAttributesWithLog(attrs);
        
        // 초기화 플래그 설정 (카트 페이지에서 호출된 경우)
        const hasInitialized = sessionStorage.getItem('cartInitialized');
        if (hasInitialized) {
          sessionStorage.setItem('cartInitializationComplete', 'true');
        }
      }
      
    } catch (error) {
      console.error('Failed to update cart note:', error);
      toast.error('Failed to update order notes');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const addItems = async (items: Array<{ variantId: string; quantity: number }>) => {
    try {
      // API 호출 전 예측적 토큰 갱신
      const tokenStatus = getTokenStatus();
      if (tokenStatus.isExpiringSoon) {
        console.log('[Cart] Token expiring soon, attempting proactive refresh...');
        const refreshSuccess = await proactiveTokenRefresh(15);
        if (!refreshSuccess) {
          console.log('[Cart] Proactive refresh failed, continuing with current token');
        }
      }
      
      const response = await fetch('/api/cart/items', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.error || 'Failed to add items';
        
        // 토큰 만료 에러 처리
        if (response.status === 401 || errorMessage.includes('session expired') || errorMessage.includes('log in again')) {
          // 현재 작업 상태 저장
          saveWorkState({
            items: items,
            currentCart: cart,
            action: 'addItems'
          });
          
          // 토큰 갱신 시도
          const refreshSuccess = await handleTokenExpiry(
            // 갱신 성공 시: 원래 작업 재시도
            async () => {
              console.log('[Cart] Token refreshed, retrying operation');
              toast.success('Session refreshed, continuing...');
              // 원래 작업 재시도
              return await addItems(items);
            },
            // 갱신 실패 시: 로그인 페이지로 이동
            () => {
              console.log('[Cart] Token refresh failed, redirecting to login');
              clearAuthCookiesClient();
              setCustomerAccessToken(null);
              window.location.href = `/api/auth/login?redirect=${encodeURIComponent(window.location.pathname)}&message=session_expired`;
            }
          );
          
          if (!refreshSuccess) {
            throw new Error('Session expired. Please wait a sec.');
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setCart(data.cart);
      const attrs = transformAttributesToObject(data.cart.attributes);
      setCartAttributesWithLog(attrs);
      toast.success('Added to cart successfully!');
      return data.cart;
    } catch (error) {
      console.error('Failed to add items:', error);
      
      // 토큰 만료 에러는 이미 처리되었으므로 다른 에러만 토스트로 표시
      if (error instanceof Error && !error.message.includes('Session expired')) {
        toast.error(error.message || 'Failed to add item to cart');
      }
      throw error;
    }
  };

  const updateQuantity = async (lineId: string, quantity: number): Promise<void> => {
    if (!cart) return;

    try {
      const response = await fetch('/api/cart/actions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'update',
          lineId,
          quantity 
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.error || 'Failed to update quantity';
        
        if (errorMessage.includes('You can only add')) {
          const match = errorMessage.match(/You can only add (\d+) (.+) to the cart/);
          if (match) {
            const maxQuantity = match[1];
            const productName = match[2];
            const userFriendlyMessage = `Insufficient stock: Maximum ${maxQuantity} available for ${productName}`;
            toast.error(userFriendlyMessage);
            throw new Error(userFriendlyMessage);
          }
        }
        toast.error(errorMessage);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setCart(data.cart);
      const attrs = transformAttributesToObject(data.cart.attributes);
      setCartAttributesWithLog(attrs);
    } catch (error) {
      console.error('Failed to update quantity:', error);
      throw error;
    }
  };

  const removeItem = async (lineId: string) => {
    if (!cart) return;

    try {
      const response = await fetch('/api/cart/actions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'remove',
          lineId 
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove item');
      }

      const data = await response.json();
      setCart(data.cart);
      const attrs = transformAttributesToObject(data.cart.attributes);
      setCartAttributesWithLog(attrs);
      toast.success('Item removed successfully');
    } catch (error) {
      console.error('Failed to remove item:', error);
      toast.error('Failed to remove item');
    }
  };

  const clearCart = async () => {
    if (!cart) return;

    try {
      const response = await fetch('/api/cart/clear', {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to clear cart');
      }

      const data = await response.json();
      setCart(data.cart);
      setCartAttributes({});
      toast.success('Cart cleared successfully');
    } catch (error) {
      console.error('Failed to clear cart:', error);
      toast.error('Failed to clear cart');
    }
  };

  const toggleCart = () => {
    setIsOpen(prev => !prev);
  };

  // Get customer access token from cookie
  useEffect(() => {
    const getAccessToken = () => {
      try {
        const cookies = document.cookie.split(';').reduce<Record<string, string>>((cookiesObject, cookie) => {
          const [name, value] = cookie.trim().split('=');
          cookiesObject[name] = value;
          return cookiesObject;
        }, {});

        if (cookies.shopify_access_token) {
          setCustomerAccessToken(cookies.shopify_access_token);
        } else {
          setCustomerAccessToken(null);
        }
      } catch (e) {
        console.error('Failed to get access token:', e);
        setCustomerAccessToken(null);
      }
    };

    // Initial check
    getAccessToken();

    // Listen for cookie changes
    const handleCookieChange = () => {
      getAccessToken();
    };

    document.addEventListener('cookiechange', handleCookieChange);
    window.addEventListener('storage', handleCookieChange);

    return () => {
      document.removeEventListener('cookiechange', handleCookieChange);
      window.removeEventListener('storage', handleCookieChange);
    };
  }, []);

  // Fetch cart on mount
  useEffect(() => {
    fetchCart();
  }, []);

  // Update buyer identity when customerAccessToken changes
  useEffect(() => {
    if (customerAccessToken && cart && !cart.buyerIdentity.customer) {
      updateBuyerIdentity();
    }
  }, [customerAccessToken, cart]);

  // Migrate old localStorage cart data (one-time migration)
  useEffect(() => {
    const migrateOldCart = async () => {
      const oldCartData = localStorage.getItem('cart');
      if (oldCartData && !cart) {
        try {
          const oldItems = JSON.parse(oldCartData);
          if (Array.isArray(oldItems) && oldItems.length > 0) {
            await addItems(oldItems.map(item => ({
              variantId: item.variantId,
              quantity: item.quantity
            })));
            localStorage.removeItem('cart');
            toast.success('Cart migrated successfully');
          }
        } catch (error) {
          console.error('Failed to migrate old cart:', error);
        }
      }
    };

    if (!isLoading && !cart) {
      migrateOldCart();
    }
  }, [isLoading, cart]);

  // Debug cart items
  useEffect(() => {
    // 디버그 로그 제거 - 프로덕션에서는 불필요
    // if (cart) {
    //   console.log('Cart lines:', cart.lines.edges.map(({ node }) => ({
    //     id: node.id,
    //     merchandise: node.merchandise.id,
    //     handle: node.merchandise.product.handle
    //   })));
    //   console.log('Cart attributes:', cartAttributes);
    // }
  }, [cart, cartAttributes]);

  // Legacy format compatibility
  const legacyItems = cart ? transformCartToLegacyFormat(cart) : [];
  const itemCount = cart ? cart.lines.edges.reduce((sum, { node }) => sum + node.quantity, 0) : 0;
  const totalPrice = cart ? parseFloat(cart.cost.totalAmount.amount) : 0;

  return {
    cart,
    items: legacyItems,
    itemCount,
    totalItems: itemCount,
    totalPrice,
    isOpen,
    isLoading,
    cartAttributes,
    updateCartAttributes,
    cartNote,
    updateCartNote,
    addItem: async (item: { variantId: string; quantity: number }) => {
      await addItems([item]);
    },
    addItems,
    updateQuantity,
    removeItem,
    removeFromCart: removeItem,
    clearCart,
    toggleCart,
    fetchCart,
  };
}